/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { compact } from 'lodash';
import { Button } from '@this/shared/ui/inputs/button';

import NotifiedUserSelectorTemplate from '@this/shared/user_selector/notified_user_selector.template';

import type SearchQuery from '@this/domain/search_query';
import type SearchQueryItem from '@this/domain/search_query_item';
import type SelectStore from '@this/domain/select_store';
import ArrangementRequestSetting from '@this/domain/organization/arrangement_request_setting2';
import ArrangementRequestInfo from '@this/domain/arrangement_request_info/arrangement_request_info';
import type { ReserveRequestMailNotificationType } from '@this/domain/organization/organization2';
import SubInfo from '@this/shared/sub_info/sub_info';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { HTTPError, dig, formatPrice } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';
import User from '../../../../domain/user/user';
import Department from '../../../../domain/department/department';
import type TravelerList from '../../../../domain/traveler/traveler_list';
import Traveler from '../../../../domain/traveler/traveler';
import ProjectList from '../../../../domain/project/project_list';
import ArrangementRequestInfoForm from './arrangement_request_info_form/arrangement_request_info_form';
import SelectOutlineWrapperTemplate from '../outline_wrapper/outline_wrapper.template';
import OutlineWrapperShortDistanceContainer from '../outline_wrapper/OutlineWrapperShortDistanceContainer';
import { createItineraryDescription, createInitialTravelers } from './utils';
import TravelerForm from './TravelerForm';
import TermsAndConditions from './TermsAndConditions';
import { fetchArrangementRequestInitialData, submitArrangementRequest } from './api';

interface Props {
  serviceId: number;
  query: SearchQuery;
  store: SelectStore;
  selectErrors: string[];
  reserveRequestMailNotificationType: ReserveRequestMailNotificationType;
  handleNextButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleSelectItem: () => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  additionalSearchErrors?: { [k in string]: string };
  handleOpenShowSearchBox: () => void;
  shortdistance: boolean;
}

interface State {
  showSearchResult: boolean;
  memo: string;
  termsAgree: boolean;
  tripForeign: boolean;
  travelers: TravelerList;
  requestInfo?: ArrangementRequestInfo;
  members: User[];
  user: User;
  submitting: boolean;
  errors: string[];
  loading: boolean;
  loadingError?: string | null;

  // このフォームで変更されないもの（固定値・props由来）
  setting: ArrangementRequestSetting;
  email: string;
  origin: string;
  destination: string;
  outdate?: string;
  outhour?: string;
  outmin?: string;
  outtype: 'arrival' | 'departure';
  homedate?: string;
  homehour?: string;
  homemin?: string;
  hometype: 'arrival' | 'departure';
  peoplenum: number;
  firstName: string;
  lastName: string;
  passportNumber: string;
  passportExpire: string;
  passportIssuedCoutry: string;
  hotelLocation: string;
  description: string;
  domesticAirExist: boolean;
  foreignExist: boolean;
  shinkansenExist: boolean;
  hotelExist: boolean;
}

const ArrangementRequestForm: React.FC<Props> = props => {
  const {
    serviceId,
    query,
    store,
    selectErrors,
    handleNextButtonClick,
    handleSelectItem,
    handleAdditionalSearch,
    additionalSearchErrors,
    handleOpenShowSearchBox,
    reserveRequestMailNotificationType,
    shortdistance
  } = props;

  // props由来の固定値をメモ化（stateでの重複を避ける）
  const fixedValues = useMemo(() => {
    const settingJson = dig(props.store, 'user', 'organization', 'arrangement_request_setting');
    const user = new User(props.store.user as any);

    return {
      setting: new ArrangementRequestSetting(settingJson),
      email: user.email || '',
      origin: '',
      destination: props.query.finalDestination() || '',
      outdate: props.query.startTime()?.date,
      outhour: props.query.startTime()?.hour,
      outmin: props.query.startTime()?.min,
      outtype: 'arrival' as const,
      homedate: props.query.endTime()?.date,
      homehour: props.query.endTime()?.hour,
      homemin: props.query.endTime()?.min,
      hometype: 'arrival' as const,
      peoplenum: props.query.peoplenum,
      firstName: '',
      lastName: '',
      passportNumber: '',
      passportExpire: '',
      passportIssuedCoutry: '',
      hotelLocation: '',
      description: createItineraryDescription(props.query.getSummaryData()),
      domesticAirExist: props.store.result.isDomesticAirResult(),
      foreignExist: props.store.result.isFlightResult(),
      shinkansenExist: props.store.result.isShinkansenResult(),
      hotelExist: props.store.result.isHotelResult(),
      user,
      initialMembers: [] as User[],
      initialTravelers: props.query.travelers || createInitialTravelers(props.query.peoplenum, user, [])
    };
  }, [props.store, props.query]);

  // Stateは固定値をfixedValuesから取得する（既存インターフェースを維持）
  const [state, setState] = useState<State>(() => ({
    // 固定値はfixedValuesから取得
    ...fixedValues,
    // 動的なUI状態のみを初期化
    showSearchResult: true,
    memo: '',
    members: fixedValues.initialMembers,
    travelers: fixedValues.initialTravelers,
    requestInfo: undefined,
    submitting: false,
    errors: [],
    loading: true,
    loadingError: null,
    termsAgree: true,
    tripForeign: true
  }));

  // Memoized callbacks
  const handleAgreementTermsCheck = useCallback(() => {
    setState(prev => ({
      ...prev,
      termsAgree: !prev.termsAgree
    }));
  }, []);

  const handleTripForeignCheck = useCallback(() => {
    setState(prev => ({
      ...prev,
      tripForeign: !prev.tripForeign
    }));
  }, []);

  const handleTravelerTypeChange = useCallback(
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === 'self') {
        const userTraveler = new Traveler(state.user);
        state.travelers.setTravelerAtIndex(i, userTraveler);
      } else {
        state.travelers.setTravelerTypeAtIndex(i, value);
      }
    },
    [state.user, state.travelers]
  );

  const handleTravelerSelect = useCallback(
    (i: number, traveler: Traveler) => {
      state.travelers.setTravelerAtIndex(i, traveler);
    },
    [state.travelers]
  );

  const handleNotifiedUserAdd = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (state.requestInfo) {
        const users = state.requestInfo.notifiedUsers;
        users.push(undefined);
        state.requestInfo.setNotifiedUsers(users);
      }
    },
    [state.requestInfo]
  );

  const handleNotifiedUserRemove = useCallback(
    (e: React.MouseEvent<HTMLElement>, i: number) => {
      e.preventDefault();
      if (state.requestInfo) {
        const users = state.requestInfo.notifiedUsers;
        users.splice(i, 1);
        state.requestInfo.setNotifiedUsers(users);
      }
    },
    [state.requestInfo]
  );

  const handleNotifiedUserSelect = useCallback(
    (i: number, traveler: Traveler) => {
      if (state.requestInfo) {
        const users = state.requestInfo.notifiedUsers;
        users[i] = traveler;
        state.requestInfo.setNotifiedUsers(users);
      }
    },
    [state.requestInfo]
  );

  const handleShowSearchResultUpdate = useCallback(() => {
    setState(prev => ({
      ...prev,
      showSearchResult: !prev.showSearchResult
    }));
  }, []);

  const handleMemoChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setState(prev => ({
      ...prev,
      memo: value
    }));
  }, []);

  const submitParams = useCallback(() => {
    if (!state.requestInfo) {
      throw new Error('RequestInfo is not loaded yet');
    }

    return {
      email: fixedValues.email,
      origin: fixedValues.origin,
      destination: fixedValues.destination,
      outdate: fixedValues.outdate,
      outhour: fixedValues.outhour,
      outmin: fixedValues.outmin,
      outtype: fixedValues.outtype,
      homedate: fixedValues.homedate,
      homehour: fixedValues.homehour,
      homemin: fixedValues.homemin,
      hometype: fixedValues.hometype,
      peoplenum: fixedValues.peoplenum,
      first_name: fixedValues.firstName,
      last_name: fixedValues.lastName,
      passport_number: fixedValues.passportNumber,
      passport_expire: fixedValues.passportExpire,
      passport_issued_country: fixedValues.passportIssuedCoutry,
      hotel_location: fixedValues.hotelLocation,
      memo: state.memo,
      itinerary_description: fixedValues.description,
      traveler_informations: state.travelers.getTravelersParam({ isRequestForm: true }),
      info: state.requestInfo.submitParams(),
      search_query_id: store.result.searchQueryId,
      result: state.showSearchResult ? store.result.confirmParams() : null,
      is_foreign: store.result.items.some(item => !item.isDomestic),
      is_trip_foreign: state.tripForeign
    };
  }, [
    state.requestInfo,
    state.travelers,
    state.memo,
    state.showSearchResult,
    state.tripForeign,
    fixedValues,
    store
  ]);

  const validateParams = useCallback((): string[] => {
    if (!state.requestInfo) {
      return ['システムエラーが発生しました。ページを再読み込みしてください。'];
    }

    const errors = state.requestInfo.validate(reserveRequestMailNotificationType);
    const travelerErrors: { [key: string]: string | undefined } = {};
    state.travelers.list.forEach(t => t.validate(travelerErrors, fixedValues.foreignExist));
    return errors.concat(compact(Object.keys(travelerErrors).map(k => travelerErrors[k])));
  }, [state.requestInfo, state.travelers, fixedValues.foreignExist, reserveRequestMailNotificationType]);

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setState(prev => ({
        ...prev,
        errors: [],
        submitting: true
      }));

      try {
        const errors = validateParams();
        if (errors.length > 0) {
          setState(prev => ({
            ...prev,
            errors,
            submitting: false
          }));
        } else {
          const res = await submitArrangementRequest(submitParams());
          location.href = res.redirect_path;
        }
      } catch (errorObject) {
        if (
          errorObject instanceof HTTPError &&
          errorObject.response?.status === 400 &&
          errorObject.response.data.errors
        ) {
          setState(prev => ({
            ...prev,
            errors: errorObject.response?.data?.errors || ['エラーが発生しました。'],
            submitting: false
          }));
        } else {
          setState(prev => ({
            ...prev,
            errors: ['通信エラーが発生しました。時間をおいて再度お試しください。'],
            submitting: false
          }));
        }
      }
    },
    [submitParams, validateParams]
  );

  const travelerInputs = useCallback(() => {
    const { user: stateUser, travelers: stateTravelers, members: stateMembers } = state;
    const isFlightCustomerInfoRequired = fixedValues.domesticAirExist || fixedValues.foreignExist;
    const isHotelInfoRequired = fixedValues.hotelExist;

    return stateTravelers.list.map((traveler, i) => (
      <TravelerForm
        key={`traveler-form-${i}`}
        traveler={traveler}
        index={i}
        user={stateUser}
        travelers={stateTravelers}
        members={stateMembers}
        isFlightCustomerInfoRequired={isFlightCustomerInfoRequired}
        isHotelInfoRequired={isHotelInfoRequired}
        onTravelerTypeChange={handleTravelerTypeChange}
        onTravelerSelect={handleTravelerSelect}
      />
    ));
  }, [
    state.user,
    state.travelers,
    state.members,
    fixedValues.domesticAirExist,
    fixedValues.foreignExist,
    fixedValues.hotelExist,
    handleTravelerTypeChange,
    handleTravelerSelect
  ]);

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      setState(prev => ({
        ...prev,
        loading: true,
        loadingError: null
      }));

      try {
        const res = await fetchArrangementRequestInitialData();
        const departments = res.departments.map(d => new Department(d));
        const projects = new ProjectList(res.projects);

        setState(prev => ({
          ...prev,
          members: res.members.map(m => new User(m)),
          requestInfo: new ArrangementRequestInfo({
            hotelExist: fixedValues.hotelExist,
            shinkansenExist: fixedValues.shinkansenExist,
            foreignExist: fixedValues.foreignExist,
            domesticAirExist: fixedValues.domesticAirExist,
            useBulkTicket: res.use_bulk_ticket,
            showEx: res.show_ex,
            travelers: prev.travelers,
            user: prev.user,
            departments,
            projects,
            carType: res.car_type,
            itemType: res.item_type,
            availableRequestForms: res.available_request_forms
          }),
          loading: false
        }));
      } catch {
        setState(prev => ({
          ...prev,
          loading: false,
          loadingError: '通信エラーが発生しました。時間をおいて再度お試しください。'
        }));
      }
    };

    fetchData();
  }, []);

  // Derived values
  const { showSearchResult } = state;
  const baseClass = 'arrangement-request-form';

  try {
    return state.loading ? (
      <Loading />
    ) : state.loadingError ? (
      <p style={getStyles().errorText}>{state.loadingError}</p>
    ) : (
      <form className={`${baseClass}`} onSubmit={handleFormSubmit}>
        <div className={`${baseClass}__form-wrap`}>
          <div style={getStyles().formBox01}>
            <div className={`${baseClass}__field`}>
              <div>検索条件</div>
              <SubInfo data={query.getSummaryData()} customFontSize="13px" />
            </div>
            {reserveRequestMailNotificationType === 'selectable_user' ? (
              <div className={`${baseClass}__field`}>
                <div>リクエストに関するメール通知対象者の選択</div>
                <div>
                  {state.requestInfo && (
                    <NotifiedUserSelectorTemplate
                      applicant={state.user}
                      users={state.requestInfo?.notifiedUsers ?? []}
                      onAdd={handleNotifiedUserAdd}
                      onSelect={handleNotifiedUserSelect}
                      onRemove={handleNotifiedUserRemove}
                    />
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className={`${baseClass}__field`}>
                  <div>出張者</div>
                  <div>{travelerInputs()}</div>
                </div>
                {state.requestInfo && (
                  <div className={`${baseClass}__field`}>
                    <ArrangementRequestInfoForm requestInfo={state.requestInfo} />
                  </div>
                )}
              </>
            )}
            <div className={`${baseClass}__field`}>
              <label htmlFor="memo-textarea">
                備考欄（希望条件・便情報など）
                <div style={getStyles().guideText}>
                  ※以下の情報をコピーして入力いただくとスムーズです
                  <br />
                  【1.種別 2.経路/場所 3.日時 4.便名/列車名/施設名 5.希望条件（券種・席種・部屋タイプなど）】
                </div>
                <textarea
                  id="memo-textarea"
                  rows={10}
                  onChange={handleMemoChange}
                  value={state.memo}
                  placeholder="例）1.航空券 2.羽田空港→福岡空港 3.04/01 07:00発 4.JAL001 5.変更可 希望"
                />
              </label>
            </div>
            <div className={`${baseClass}__field`}>
              <label htmlFor="trip-foreign-checkbox">
                <input
                  type="checkbox"
                  id="trip-foreign-checkbox"
                  className="trip_foreign"
                  checked={state.tripForeign}
                  onChange={handleTripForeignCheck}
                />
                海外出張
              </label>
            </div>
          </div>
          <div className={`${baseClass}__search-result-section`}>
            {showSearchResult && (
              <div style={getStyles().outlineWrapper}>
                {shortdistance ? (
                  <OutlineWrapperShortDistanceContainer
                    query={query}
                    store={store}
                    selectErrors={selectErrors}
                    packageErrors={store.packageErrors[store.result.airline]}
                    handleSelectItem={handleSelectItem}
                    handleAdditionalSearch={handleAdditionalSearch}
                    additionalSearchErrors={additionalSearchErrors}
                    handleNextButtonClick={handleNextButtonClick}
                    submitting={state.submitting}
                    isRequestForm
                    handleOpenShowSearchBox={handleOpenShowSearchBox}
                  />
                ) : (
                  <SelectOutlineWrapperTemplate
                    query={query}
                    store={store}
                    selectErrors={selectErrors}
                    packageErrors={store.packageErrors[store.result.airline]}
                    handleSelectItem={handleSelectItem}
                    handleAdditionalSearch={handleAdditionalSearch}
                    additionalSearchErrors={additionalSearchErrors}
                    handleNextButtonClick={handleNextButtonClick}
                    submitting={state.submitting}
                    isRequestForm
                    handleOpenShowSearchBox={handleOpenShowSearchBox}
                  />
                )}
              </div>
            )}
            <label htmlFor="show-search-result-checkbox">
              <input
                type="checkbox"
                id="show-search-result-checkbox"
                checked={showSearchResult}
                onChange={handleShowSearchResultUpdate}
              />
              この旅程をリクエストに表示する。
            </label>
          </div>
        </div>
        <div className={`${baseClass}__field`}>
          <small>{`※こちらのリクエストフォームからご依頼いただいた場合、別途手数料として${formatPrice(
            fixedValues.setting.fee
          )}（税別）が発生します。`}</small>
        </div>
        {state.errors.length > 0 && (
          <div className={`${baseClass}__field`}>
            {state.errors.map(e => (
              <p key={`${e}`} style={getStyles().errorText}>
                {e}
              </p>
            ))}
          </div>
        )}
        <div className={`${baseClass}__field`} style={getStyles().smallText}>
          予約（決済）完了後からご依頼いただいた各商品のキャンセル規程が適用されます。
          <br />
          ご依頼いただいた各商品について、在庫情報のタイムラグ等によりご希望に添えなかった場合は代替案をご連絡いたしますのでご了承ください。
          <br />
          <TermsAndConditions serviceId={serviceId} />
          <br />
          本依頼を行う者は、本サービスを正当に利用する権限を有することを保証します。
          <br />
          <label htmlFor="terms-agree-checkbox">
            <input
              id="terms-agree-checkbox"
              type="checkbox"
              className="terms_agree"
              checked={state.termsAgree}
              onChange={handleAgreementTermsCheck}
            />
            上記に同意する
          </label>
        </div>
        <div className={`${baseClass}__field`}>
          <Button type="submit" disabled={state.submitting || !state.termsAgree} style={getStyles().submitButton}>
            {state.submitting ? (
              <span style={getStyles().submitButtonContent}>
                送信中...
                <Loading size="small" />
              </span>
            ) : (
              '送信'
            )}
          </Button>
        </div>
      </form>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

function getStyles() {
  return {
    smallText: {
      fontSize: '11px'
    } as React.CSSProperties,
    errorText: {
      color: '#a72c09'
    } as React.CSSProperties,
    outlineWrapper: {
      marginBottom: '10px',
      background: '#fff'
    } as React.CSSProperties,
    formBox01: {
      width: '100%'
    } as React.CSSProperties,
    submitButton: {
      width: '100%'
    } as React.CSSProperties,
    submitButtonContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    } as React.CSSProperties,
    guideText: {
      fontSize: '12px',
      color: '#666',
      marginTop: '8px',
      marginBottom: '4px'
    } as React.CSSProperties
  } as const;
}

export default ArrangementRequestForm;
